import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, MessageBox } from '@reservamos/elements';
import useStripeApplePay from 'hooks/strategies/useStripeApplePay';
import usePurchase from 'hooks/store/usePurchase';
import CheckList from 'ui/atoms/CheckList';
import WalletButtonContainer from '../shared/WalletButtonContainer';
import './StripeApplePayForm.scss';

/**
 * StripeApplePayForm component.
 * Render the Stripe Express Checkout Element (ECE) container for Apple Pay along with
 * payment instructions and loading/error states.
 *
 * The Apple Pay button is rendered by the ECE itself inside the container; the main
 * "Finalizar compra" checkout button is not used for this method. Checkout validations are
 * gated through onPaymentButtonClick before the wallet sheet opens (Adyen AP / PayPal v6 pattern).
 *
 * @param {Object} props - onPaymentButtonClick.
 * @param {Function} props.onPaymentButtonClick - Callback when the Apple Pay button is clicked;
 *   receives `{ resolve, reject }` to gate the wallet sheet on checkout validations.
 * @param {boolean} [props.quickCheckoutAgreementsBlocked] - Overlay a Quick Purchase legal error.
 * @param {Function} [props.beforeWalletPayment] - Current-state check before the wallet payment dispatch.
 * @returns {JSX.Element} Apple Pay payment form with the ECE native button.
 */
const StripeApplePayForm = ({
  onPaymentButtonClick,
  quickCheckoutAgreementsBlocked,
  beforeWalletPayment,
}) => {
  const { t } = useTranslation('payment');
  const {
    isUpdating,
    updatingInsurance,
    updatingServiceLevel,
    updatingCarbonOffset,
    applyingDiscount,
    isUpdatingFlatFare,
  } = usePurchase();

  const { containerRef, isLoading, error, isAvailable } = useStripeApplePay({
    onPaymentButtonClick,
    ...(beforeWalletPayment && { beforeWalletPayment }),
  });

  if (error && !isLoading) {
    return (
      <div className="checkout-section">
        <MessageBox type="error">
          <Text>
            {t(
              `apple_pay_status.${error}`,
              t('apple_pay_status.default_error', 'Apple Pay is not available at this time'),
            )}
          </Text>
        </MessageBox>
      </div>
    );
  }

  // Hide the native Apple Pay button while the purchase total is being recalculated by any
  // ancillary toggle (insurance, service level / "Boleto flexible", carbon offset, flat fare) or
  // a discount code. The ECE native button bypasses the disabled-button guard the standard
  // checkout button already has in Checkout.js, so it must be gated here explicitly.
  const isUpdatingTotal =
    updatingInsurance ||
    updatingServiceLevel ||
    updatingCarbonOffset ||
    applyingDiscount ||
    isUpdatingFlatFare;
  const showLoading = isLoading || isUpdating || isUpdatingTotal || !isAvailable;

  return (
    <div className="checkout-section applepay-form stripe-applepay-form">
      {showLoading ? (
        <div className="applepay-loading">
          <Text>
            {isUpdating
              ? t('apple_pay_status.processing_payment', 'Processing payment...')
              : t('apple_pay_status.initializing', 'Initializing Apple Pay...')}
          </Text>
        </div>
      ) : (
        <Spacing vertical>
          <CheckList
            title={t('label.pay_with_apple_pay', 'Pay with Apple Pay')}
            labels={[
              t('apple_pay_instructions.first', 'Click the Apple Pay button'),
              t('apple_pay_instructions.second', 'Authorize the payment with Face ID or Touch ID'),
              t('apple_pay_instructions.third', 'Your purchase will be processed automatically'),
            ]}
          />
        </Spacing>
      )}

      <WalletButtonContainer
        ref={containerRef}
        onPaymentButtonClick={onPaymentButtonClick}
        quickCheckoutAgreementsBlocked={quickCheckoutAgreementsBlocked}
        hidden={showLoading}
        label={t('label.pay_with_apple_pay', 'Pay with Apple Pay')}
        containerClassName="stripe-applepay-button-container"
      />
    </div>
  );
};

StripeApplePayForm.propTypes = {
  onPaymentButtonClick: PropTypes.func.isRequired,
  quickCheckoutAgreementsBlocked: PropTypes.bool,
  beforeWalletPayment: PropTypes.func,
};

export default StripeApplePayForm;
